import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import client from "@/lib/api";
import { saveAction } from "@/lib/saveAction";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { Loading } from "@/components/shared/UI";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { LayoutGrid, Plus, RotateCcw, Check } from "lucide-react";
import { BLOCKS, blockFromPreset, defaultLayout, noticeboardBlock } from "@/lib/dashboardBlocks";
import { DashContext } from "@/components/dashboard/DashContext";
import { BlockFrame } from "@/components/dashboard/BlockFrame";
import { BlockSettingsDialog } from "@/components/dashboard/BlockSettingsDialog";
import { AddBlockDialog } from "@/components/dashboard/AddBlockDialog";

const PREF_KEY = "dashboard_layout";
const valid = (l) => Array.isArray(l) && l.every((b) => b && b.id && BLOCKS[b.type]);

function useLayout() {
  const { user, patchPreferences } = useAuth();
  const [layout, setLayout] = useState(() => (valid(user?.preferences?.[PREF_KEY]) ? user.preferences[PREF_KEY] : defaultLayout()));
  const timer = useRef(null);
  const save = useCallback((next, extra = {}) => {
    setLayout(next);
    patchPreferences({ [PREF_KEY]: next, ...extra });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => client.put("/me/preferences", { preferences: { [PREF_KEY]: next, ...extra } }).catch(() => toast.error("Could not save dashboard")), 500);
  }, [patchPreferences]);
  useEffect(() => {
    if (user?.preferences?.noticeboard_seeded) return;
    const has = layout.some((b) => b.type === "noticeboard");
    save(has ? layout : [noticeboardBlock(), ...layout], { noticeboard_seeded: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [layout, save];
}

function useDashData() {
  const { openTask, dataVersion } = useUI();
  const [data, setData] = useState(null);
  const load = useCallback(() => client.get("/dashboard").then(({ data }) => setData(data)).catch(() => setData({})), []);
  useEffect(() => { load(); }, [load, dataVersion]);
  const cardProps = useMemo(() => {
    const completeTask = async (task) => {
      try { await client.post(`/actions/${task.id}/complete`, {}); toast.success("Task done"); load(); }
      catch (e) { toast.error("Could not complete task"); }
    };
    const toggleSubtask = async (task, subId) => {
      const subtasks = (task.subtasks || []).map((s) => (s.id === subId ? { ...s, completed: !s.completed } : s));
      try { await saveAction(task, { ...task, subtasks }); load(); }
      catch (e) { toast.error("Could not update sub-task"); }
    };
    const skip = new Set(["id", "created_at", "updated_at", "created_by_id", "updated_by_id", "completed_date", "completed_at", "completed_by_id", "completed_by_name", "is_overdue", "organisation_name", "property_name", "contact_name", "assigned_to_names", "created_by_name", "subtask_total", "subtask_done"]);
    const duplicateTask = (task) => {
      const prefill = Object.fromEntries(Object.entries(task).filter(([k]) => !skip.has(k)));
      prefill.title = `${task.title || "Task"} (Copy)`;
      prefill.status = "To Do";
      prefill.subtasks = (task.subtasks || []).map((s) => ({ title: s.title, assigned_to_id: s.assigned_to_id || null, completed: false }));
      openTask({ prefill, onSuccess: load });
    };
    return { onEdit: (t) => openTask(t, load), onComplete: completeTask, onRefresh: load, onToggleSubtask: toggleSubtask, onDuplicate: duplicateTask };
  }, [load, openTask]);
  return { data, reload: load, cardProps };
}

function Toolbar({ editing, setEditing, onAdd, onReset }) {
  if (!editing) return <Button variant="outline" onClick={() => setEditing(true)} data-testid="dashboard-customise-btn"><LayoutGrid className="mr-1.5 h-4 w-4" />Customise</Button>;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="outline" onClick={onAdd} data-testid="dashboard-add-block-btn"><Plus className="mr-1.5 h-4 w-4" />Add block</Button>
      <Button variant="ghost" onClick={onReset} data-testid="dashboard-reset-btn"><RotateCcw className="mr-1.5 h-4 w-4" />Reset</Button>
      <Button onClick={() => setEditing(false)} data-testid="dashboard-done-btn"><Check className="mr-1.5 h-4 w-4" />Done</Button>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const dash = useDashData();
  const [layout, save] = useLayout();
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [settingsFor, setSettingsFor] = useState(null);
  const gridRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const update = (id, patch) => save(layout.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const remove = (id) => save(layout.filter((b) => b.id !== id));
  const add = (preset) => { const b = blockFromPreset(preset); save([...layout, b]); setAdding(false); toast.success(`${preset.label} added`); };
  const reset = () => { if (window.confirm("Reset your dashboard to the default layout?")) save(defaultLayout()); };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = layout.findIndex((b) => b.id === active.id), to = layout.findIndex((b) => b.id === over.id);
    save(arrayMove(layout, from, to));
  };
  const h = new Date().getHours();
  const greeting = h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  if (!dash.data) return <Loading />;

  return (
    <DashContext.Provider value={dash}>
      <div className="space-y-6" data-testid="dashboard-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{greeting}, {user?.name?.split(" ")[0]}</h1>
            <p className="mt-1 text-sm text-slate-500">{editing ? "Drag blocks to reorder, drag the blue handles to resize, or use the gear for filters." : "Here's what needs your attention today."}</p>
          </div>
          <Toolbar editing={editing} setEditing={setEditing} onAdd={() => setAdding(true)} onReset={reset} />
        </div>
        {layout.length === 0 && (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 py-16 text-center" data-testid="dashboard-empty">
            <p className="text-sm text-slate-500">Your dashboard is empty.</p>
            <Button className="mt-3" onClick={() => { setEditing(true); setAdding(true); }} data-testid="dashboard-empty-add-btn"><Plus className="mr-1.5 h-4 w-4" />Add a block</Button>
          </div>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={layout.map((b) => b.id)} strategy={rectSortingStrategy}>
            <div ref={gridRef} className="dash-grid" data-testid="dashboard-grid">
              {layout.map((b) => (
                <BlockFrame key={b.id} block={b} editing={editing} gridRef={gridRef}
                  onUpdate={(p) => update(b.id, p)} onRemove={() => remove(b.id)} onSettings={() => setSettingsFor(b)} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
      <AddBlockDialog open={adding} onClose={() => setAdding(false)} onAdd={add} />
      {settingsFor && <BlockSettingsDialog block={settingsFor} onClose={() => setSettingsFor(null)} onSave={(d) => { update(d.id, d); setSettingsFor(null); }} />}
    </DashContext.Provider>
  );
}
